import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Mail, X } from 'lucide-react';
import type { RfqProject, RfqSubmission, SupplierOrg, VendorWorkType } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import {
  buildRfqQuoteApprovalEmailHtml,
  defaultRfqQuoteApprovalEmailMessage,
  rfqQuoteApprovalEmailSubject,
} from '../../utils/rfqQuoteApprovalEmailPreview';
import { rfqProjectEmailDescriptor } from '../../utils/rfqPublishEmailPreview';
import { sendSupplierQuoteApprovalEmail } from '../../utils/sendSupplierQuoteApprovalEmail';
import {
  recipientFirstNameForOrg,
  resolveApprovalRecipientsForOrgs,
  resolveOrgsMissingRfqNotificationFirstName,
} from '../../utils/vendorOrgSummary';
import './SupplierQuoteApprovalModal.css';

export type SupplierQuoteApprovalTarget =
  | {
      kind: 'timber';
      orgId: string;
      orgName: string;
      sub: RfqSubmission;
      quoteLabel: string;
    }
  | {
      kind: 'cnc';
      orgId: string;
      orgName: string;
      sub?: RfqSubmission;
      quoteLabel: string;
    }
  | {
      kind: 'other';
      orgId: string;
      orgName: string;
      sub: RfqSubmission;
      quoteLabel: string;
    };

export type SupplierQuoteApprovalModalProps = {
  open: boolean;
  target: SupplierQuoteApprovalTarget | null;
  project: RfqProject;
  workType: VendorWorkType;
  projectNumber?: string;
  projectName?: string;
  supplierOrgById: Map<string, SupplierOrg>;
  onClose: () => void;
  onApproved: () => Promise<void>;
  /** Quote is already approved — only send the supplier notice. */
  emailOnly?: boolean;
  /** Runs after the email step finishes and this dialog closes. Opens payment terms / rejection notices. */
  onFinished?: () => void;
};

const SupplierQuoteApprovalModal: React.FC<SupplierQuoteApprovalModalProps> = ({
  open,
  target,
  project,
  workType,
  projectNumber,
  projectName,
  supplierOrgById,
  onClose,
  onApproved,
  emailOnly = false,
  onFinished,
}) => {
  const { user } = useAuth();
  const [customMessage, setCustomMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const approvalSavedRef = useRef(false);

  const orgId = target?.orgId ?? '';
  const orgName = target?.orgName ?? '';
  const quoteLabel = target?.quoteLabel ?? '';
  const dealTitle = project.dealTitle || project.dealId;
  const senderName = (user?.name || user?.email || '').trim();
  const recipientFirstName = orgId ? recipientFirstNameForOrg(orgId, supplierOrgById) : '';

  useEffect(() => {
    if (!open || !target) return;
    approvalSavedRef.current = false;
    setSendEmail(true);
    setError(null);
    setCustomMessage(
      defaultRfqQuoteApprovalEmailMessage({
        recipientFirstName: recipientFirstName || 'there',
        quoteLabel: target.quoteLabel,
        workType,
      }),
    );
  }, [open, target, workType, recipientFirstName]);

  const recipients = useMemo(() => {
    if (!orgId) return [];
    return resolveApprovalRecipientsForOrgs([orgId], supplierOrgById);
  }, [orgId, supplierOrgById]);

  const recipientRow = recipients[0];
  const canSendEmail = !!recipientRow?.emails.length && !!recipientRow.notificationFirstName;
  const missingFirstName = orgId
    ? resolveOrgsMissingRfqNotificationFirstName([orgId], supplierOrgById)
    : [];

  const emailSubject = useMemo(
    () =>
      rfqQuoteApprovalEmailSubject(
        projectNumber,
        rfqProjectEmailDescriptor({ descriptor: projectName, dealTitle }),
        workType,
      ),
    [projectNumber, projectName, dealTitle, workType],
  );

  const emailHtml = useMemo(
    () =>
      buildRfqQuoteApprovalEmailHtml({
        recipientFirstName: recipientFirstName || 'there',
        vendorOrgName: orgName,
        senderName,
        dealTitle,
        projectNumber,
        projectName,
        workType,
        customMessage,
        quoteLabel,
      }),
    [
      recipientFirstName,
      orgName,
      senderName,
      dealTitle,
      projectNumber,
      projectName,
      workType,
      customMessage,
      quoteLabel,
    ],
  );

  const finish = () => {
    onClose();
    if (!emailOnly) onFinished?.();
  };

  const handleClose = () => {
    if (submitting) return;
    if (approvalSavedRef.current) finish();
    else onClose();
  };

  const handleConfirm = async () => {
    if (!target || !project.id) return;
    const shouldSend = emailOnly || sendEmail;
    if (shouldSend && !canSendEmail) {
      setError('Add RFQ notification contact info in Vendor Management before sending email.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (!emailOnly && !approvalSavedRef.current) {
        await onApproved();
        approvalSavedRef.current = true;
      }
      if (shouldSend && canSendEmail) {
        const result = await sendSupplierQuoteApprovalEmail({
          supplierOrgId: target.orgId,
          dealId: project.dealId,
          dealTitle,
          projectNumber,
          projectName,
          workType,
          quoteLabel: target.quoteLabel,
          customEmailMessage: customMessage.trim() || undefined,
          senderName,
          submissionId:
            target.kind === 'timber' || target.kind === 'other'
              ? target.sub.id
              : target.sub?.id,
          rfqProjectId: project.id,
        });
        if (!result.ok && !result.skipped) {
          throw new Error(result.reason || 'Approval saved but email could not be sent.');
        }
      }
      finish();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Could not complete approval.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !target) return null;

  return (
    <div
      className="ops-modal-overlay sqam-overlay"
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="ops-modal sqam-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sqam-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="ops-modal-header sqam-header">
          <div className="sqam-title-block">
            <CheckCircle2 size={18} aria-hidden className="sqam-title-icon" />
            <h3 id="sqam-title">{emailOnly ? 'Send approval email' : 'Approve quote & notify supplier'}</h3>
          </div>
          <button type="button" className="ops-modal-close" aria-label="Close" onClick={handleClose} disabled={submitting}>
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body sqam-body">
          <p className="sqam-lead">
            {emailOnly ? (
              <>
                <strong>{quoteLabel}</strong> from <strong>{orgName}</strong> is already approved. Review the
                notification email below, then send it.
              </>
            ) : (
              <>
                You are approving <strong>{quoteLabel}</strong> from <strong>{orgName}</strong>. Review the notification
                email below before confirming.
              </>
            )}
          </p>

          <section className="sqam-panel">
            <h4 className="sqam-section-label">Recipient</h4>
            {recipientRow ? (
              <p className={`sqam-recipient${!canSendEmail ? ' sqam-recipient--warn' : ''}`}>
                {recipientRow.notificationFirstName ? (
                  <>Greeting: {recipientRow.notificationFirstName}</>
                ) : (
                  <strong>Missing RFQ notification first name</strong>
                )}
                {recipientRow.emails.length ? (
                  <> · {recipientRow.emails.join(', ')}</>
                ) : (
                  <> · No notification email on file</>
                )}
              </p>
            ) : null}
            {missingFirstName.length > 0 && (
              <p className="sqam-warn" role="status">
                <AlertCircle size={14} aria-hidden />
                Add a first name under RFQ notifications in Vendor Management for {orgName}.
              </p>
            )}
            {recipientRow && !recipientRow.emails.length && (
              <p className="sqam-warn" role="status">
                <AlertCircle size={14} aria-hidden />
                No RFQ notification email on file for {orgName}.
                {emailOnly ? '' : ' You can still approve without sending email.'}
              </p>
            )}
          </section>

          <section className="sqam-panel">
            {!emailOnly && (
              <label className="sqam-send-toggle">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  disabled={submitting}
                  onChange={e => setSendEmail(e.target.checked)}
                />
                <span>Send approval email to supplier</span>
              </label>
            )}

            {(emailOnly || sendEmail) && (
              <>
                <div className="sqam-subject">
                  <span className="sqam-field-label">Subject</span>
                  <span>{emailSubject}</span>
                </div>
                <div className="sqam-compose">
                  <label className="sqam-field-label" htmlFor="sqam-message">
                    Message
                  </label>
                  <p className="sqam-hint">Adjust the message suppliers receive. The greeting updates from their RFQ contact profile.</p>
                  <textarea
                    id="sqam-message"
                    className="ops-modal-input sqam-textarea"
                    rows={5}
                    value={customMessage}
                    disabled={submitting}
                    onChange={e => setCustomMessage(e.target.value)}
                  />
                </div>
                <div className="sqam-preview-wrap">
                  <div className="sqam-preview-head">
                    <Mail size={14} aria-hidden />
                    <span>Email preview</span>
                  </div>
                  <iframe
                    className="sqam-preview-frame"
                    title={`Approval email preview for ${orgName}`}
                    srcDoc={emailHtml}
                    sandbox="allow-same-origin"
                  />
                </div>
              </>
            )}
          </section>

          {error && (
            <p className="sqam-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </p>
          )}
        </div>

        <footer className="ops-modal-footer sqam-footer">
          <button type="button" className="ops-hide-completed-btn" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ops-new-btn"
            disabled={submitting || ((emailOnly || sendEmail) && !canSendEmail)}
            onClick={() => void handleConfirm()}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="sqam-spin" aria-hidden /> {emailOnly ? 'Sending…' : 'Approving…'}
              </>
            ) : emailOnly ? (
              'Send approval email'
            ) : sendEmail ? (
              'Approve & send email'
            ) : (
              'Approve without email'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SupplierQuoteApprovalModal;
